import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import AchievementsPanel from '../components/AchievementsPanel';
import StudyAnalytics from '../components/StudyAnalytics';
import HabitSettings from '../components/HabitSettings';
import habitService from '../services/habitService';
import { HabitSkeleton } from '../components/SkeletonLoader';

const HabitHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [habitData, setHabitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadHabitData();
  }, [user, navigate]);

  const loadHabitData = () => {
    const data = habitService.getHabitData();
    setHabitData(data);
    setTimeout(() => setLoading(false), 1000);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'achievements', label: 'Achievements', icon: '🏆' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  const recordSampleSession = () => {
    const sessionData = {
      duration: 2.5,
      subjects: ['Mathematics', 'Physics'],
      activities: ['practice', 'review'],
      completed: true
    };
    
    const updatedData = habitService.recordStudySession(sessionData);
    setHabitData(updatedData);
  };

  if (loading) {
    return <HabitSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-6">
        {/* Compact Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
            🎯 Habit Hub
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track habits, earn achievements, build consistency
          </p>
        </motion.div>

        {/* Compact Stats Row */}
        {habitData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 mb-6"
          >
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {habitData.currentStreak}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Current Streak
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {habitData.longestStreak}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Longest Streak
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {habitData.totalStudyDays}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Study Days
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {habitData.achievements.length}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Achievements
                </div>
              </div>
            </div>

            {/* Compact Demo Button */}
            <div className="mt-4 text-center">
              <button
                onClick={recordSampleSession}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-sm"
              >
                🎮 Record Sample Session
              </button>
            </div>
          </motion.div>
        )}

        {/* Compact Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md transition-all duration-200 text-sm ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Activity - Compact */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                  📅 Recent Activity
                </h3>
                <div className="space-y-2">
                  {habitData?.studyHistory.slice(-5).reverse().map((session, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                          {new Date(session.date).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {session.subjects.join(', ')} • {session.duration}h
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {session.completed ? '✅' : '⏳'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Weekly Progress - Compact */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                  📊 Weekly Goals
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Study Days</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                      {habitData?.weeklyGoals.studyDays || 5} days
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Study Hours</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                      {habitData?.weeklyGoals.studyHours || 20}h
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Subjects</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                      {habitData?.weeklyGoals.subjects || 3} subjects
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
              <AchievementsPanel />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
              <StudyAnalytics />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
              <HabitSettings />
            </div>
          )}
        </motion.div>

        {/* Compact Features Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 text-center mb-6">
            🚀 Key Features
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">🔥</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Streak Tracking
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Build study streaks with visual progress tracking.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">🏆</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Achievements
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Earn badges and coins for milestones.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">📈</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Analytics
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Detailed study patterns and insights.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">⚙️</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Custom Goals
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Set personalized weekly study goals.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">🔔</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Smart Reminders
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Configure study reminders and notifications.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl mb-2">💡</div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Recommendations
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Personalized study tips and advice.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HabitHub; 